import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchPlans } from './billing-client';

/** How long after the first page of a session the price list is fetched. */
export const PLANS_WARMUP_DELAY_MS = 3_000;

/**
 * THE TARIFFS ARE IN THE CACHE BEFORE ANYONE ASKS FOR THEM.
 *
 * A direct load of /plans carries the price list in its HTML, but most buyers
 * reach it by an in-app link — a locked card on a lot, the upgrade banner — and
 * then it is a request made at the exact moment they decided to look at prices.
 * Prod, 2026-10-02 21:21 (PostHog 01a0fd5e-…): a buyer 15 minutes into the site
 * tapped a locked card, his connection stalled for 71 s (every request did —
 * ours, Google's, Facebook's), /plans showed skeletons for the 23 s he stayed on
 * it, and he left without having seen a price.
 *
 * One small request a few seconds after the first page of a session, kept for as
 * long as the tab lives; /plans then opens on the list at once and refreshes it
 * in the background (and keeps it on screen if that refresh fails — see the note
 * on the query in `routes/plans.tsx`). `prefetchQuery` never throws, and a list
 * that is already cached and fresh is not fetched again.
 */
export function usePlansWarmup(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const id = window.setTimeout(() => {
      void queryClient.prefetchQuery({
        queryKey: ['plans'],
        queryFn: fetchPlans,
        staleTime: 10 * 60_000,
        gcTime: Infinity,
      });
    }, PLANS_WARMUP_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [queryClient]);
}
